//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  CarouselNavigationPosition,
  CtaButtonStyle,
  IExperienceDto,
  IconStyle,
} from '@apis/experiences/experiencesApiModels';
import { ISelfServeGallery } from '@apis/galleries/galleriesApiModels';

export enum FanreelCustomizationActionType {
  CLEAR_SNIPPET = 0,
  DEPLOY = 1,
  DEPLOYMENT_COMPLETE = 2,
  GENERATE_PREVIEW = 3,
  LOAD = 4,
  NEXT_PAGE = 5,
  PREV_PAGE = 6,
  SET_PREVIEW_SNIPPET = 7,
  SET_PAGE = 8,
  START_ASYNC = 9,
  STOP_ASYNC = 10,
  UPDATE_CUSTOMIZATION = 11,
  UPDATE_HAS_UNSAVED_CHANGES = 12,
}

export enum FanreelCustomizationPages {
  MEDIA = 'Media',
  LAYOUT = 'Layout',
  CUSTOMIZE = 'Customize',
  UPLOADER = 'Uploader',
  LOCALIZE = 'Localize',
  SETUP = 'Setup',
}

export enum FanreelCustomizationSortValues {
  MODERATION = 'Moderation',
  OPTIMIZED = 'Optimized',
  LATEST = 'Latest',
}

export const FanreelCustomizationSortLabels = {
  [FanreelCustomizationSortValues.MODERATION]: 'Moderation Time',
  [FanreelCustomizationSortValues.OPTIMIZED]: 'Optimized',
  [FanreelCustomizationSortValues.LATEST]: 'Posted Time',
};

export function generateCustomizationSortOptions() {
  return [
    {
      label:
        FanreelCustomizationSortLabels[
          FanreelCustomizationSortValues.MODERATION
        ],
      value: FanreelCustomizationSortValues.MODERATION,
    },
    {
      label:
        FanreelCustomizationSortLabels[
          FanreelCustomizationSortValues.OPTIMIZED
        ],
      value: FanreelCustomizationSortValues.OPTIMIZED,
    },
    {
      label:
        FanreelCustomizationSortLabels[FanreelCustomizationSortValues.LATEST],
      value: FanreelCustomizationSortValues.LATEST,
    },
  ];
}

export enum FanreelCustomizationImageAspectRatios {
  ORIGINAL = 'Original',
  SQUARE = 'Square',
}

export enum FanreelCustomizationCtaType {
  FILLED = 'Filled Button',
  OUTLINED = 'Outlined Button',
  TEXT = 'Text Only',
}

export function generateCustomizationCtaTypeOptions() {
  return [
    {
      label: FanreelCustomizationCtaType.FILLED,
      value: FanreelCustomizationCtaType.FILLED,
    },
    {
      label: FanreelCustomizationCtaType.OUTLINED,
      value: FanreelCustomizationCtaType.OUTLINED,
    },
    {
      label: FanreelCustomizationCtaType.TEXT,
      value: FanreelCustomizationCtaType.TEXT,
    },
  ];
}

export const FanreelCustomizationButtonShapeLabels = {
  [CtaButtonStyle.PILL]: 'Pill',
  [CtaButtonStyle.RECTANGLE]: 'Rectangle',
};

export function generateCustomizationButtonShapeOptions() {
  return [
    {
      label: FanreelCustomizationButtonShapeLabels[CtaButtonStyle.RECTANGLE],
      value: CtaButtonStyle.RECTANGLE,
    },
    {
      label: FanreelCustomizationButtonShapeLabels[CtaButtonStyle.PILL],
      value: CtaButtonStyle.PILL,
    },
  ];
}

export const FanreelCustomizationCarouselNavigationPositionLabels = {
  [CarouselNavigationPosition.INLINE_OVERLAP]: 'Inside Carousel',
  [CarouselNavigationPosition.INLINE_OUTSIDE]: 'Outside Carousel',
};

export function generateCustomizationCarouselNavigationPositionOptions() {
  return [
    {
      label:
        FanreelCustomizationCarouselNavigationPositionLabels[
          CarouselNavigationPosition.INLINE_OUTSIDE
        ],
      value: CarouselNavigationPosition.INLINE_OUTSIDE,
    },
    {
      label:
        FanreelCustomizationCarouselNavigationPositionLabels[
          CarouselNavigationPosition.INLINE_OVERLAP
        ],
      value: CarouselNavigationPosition.INLINE_OVERLAP,
    },
  ];
}

export enum FanreelCustomizationCarouselNavigationArrowStyle {
  ARROW = 'Arrow',
  ARROW_WITH_BUTTON = 'Arrow with Button',
}

export function generateCustomizationCarouselNavigationArrowStyleOptions() {
  return [
    {
      label: FanreelCustomizationCarouselNavigationArrowStyle.ARROW,
      value: FanreelCustomizationCarouselNavigationArrowStyle.ARROW,
    },
    {
      label: FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON,
      value: FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON,
    },
  ];
}

export const FanreelCustomizationCarouselNavigationButtonShapeLabels = {
  [IconStyle.ROUND]: 'Rounded',
  [IconStyle.RECTANGLE]: 'Rectangle',
};

export function generateCustomizationCarouselNavigationButtonShapeOptions() {
  return [
    {
      label:
        FanreelCustomizationCarouselNavigationButtonShapeLabels[
          IconStyle.ROUND
        ],
      value: IconStyle.ROUND,
    },
    {
      label:
        FanreelCustomizationCarouselNavigationButtonShapeLabels[
          IconStyle.RECTANGLE
        ],
      value: IconStyle.RECTANGLE,
    },
  ];
}

export interface IImageAspectRatioOption {
  label: FanreelCustomizationImageAspectRatios;
  value: FanreelCustomizationImageAspectRatios;
}

export class FanreelCustomizationAction {
  public typ: FanreelCustomizationActionType;
  public payload: any;

  constructor(typ: FanreelCustomizationActionType, payload: unknown = {}) {
    this.typ = typ;
    this.payload = payload;
  }
}

export function convertGalleryToExperience(
  gallery: ISelfServeGallery,
): IExperienceDto {
  return {
    experienceDataSource: gallery.reelId,
    experienceDataSourceId: gallery.dataSourceId,
    experienceLayout: gallery.layoutType,
    experienceType: gallery.containerType,
    experienceCustomizationOptions: gallery.customizationOptions,
  };
}

export enum HoverStateTheme {
  DARK = 'Dark',
  LIGHT = 'Light',
  CUSTOM = 'Custom',
}

export enum HoverThemeColors {
  BLACK = '#000000',
  WHITE = '#FFFFFF',
}

export enum ModalColors {
  BLACK = '#000000',
  WHITE = '#FFFFFF',
}
